/**
 * TBMuktBharat 2026 Premium UI Components
 * World-class components inspired by Instagram, Apple Health, Stripe, Linear
 */

import React, { forwardRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

// ================================================================================
// UTILITY FUNCTIONS
// ================================================================================
export const formatCount = (count: number): string => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`
  return String(count)
}

export const formatTimeAgo = (date: string | Date): string => {
  const now = new Date()
  const then = new Date(date)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  if (diffWeeks < 4) return `${diffWeeks}w`
  if (diffMonths < 12) return `${diffMonths}mo`
  return then.toLocaleDateString()
}

// ================================================================================
// PREMIUM BUTTON COMPONENT
// ================================================================================
const buttonVariantsCVA = cva(
  'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        primary: 'bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700 shadow-sm hover:shadow-md focus-visible:ring-primary-500',
        secondary: 'bg-secondary-500 text-white hover:bg-secondary-600 active:bg-secondary-700 shadow-sm hover:shadow-md focus-visible:ring-secondary-500',
        accent: 'bg-accent-500 text-white hover:bg-accent-600 active:bg-accent-700 shadow-sm hover:shadow-md focus-visible:ring-accent-500',
        outline: 'border-2 border-neutral-200 dark:border-neutral-700 bg-transparent hover:bg-neutral-50 dark:hover:bg-neutral-800 text-foreground focus-visible:ring-neutral-500',
        ghost: 'bg-transparent hover:bg-neutral-100 dark:hover:bg-neutral-800 text-foreground',
        link: 'bg-transparent underline-offset-4 hover:underline text-primary-500',
        danger: 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700 shadow-sm hover:shadow-md focus-visible:ring-red-500',
        success: 'bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700 shadow-sm hover:shadow-md focus-visible:ring-emerald-500',
        gradient: 'bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 text-white shadow-md hover:shadow-lg',
      },
      size: {
        xs: 'h-7 px-2.5 text-xs rounded-md',
        sm: 'h-8 px-3 text-sm rounded-lg',
        md: 'h-10 px-4 text-sm rounded-lg',
        lg: 'h-12 px-6 text-base rounded-xl',
        xl: 'h-14 px-8 text-lg rounded-xl',
        icon: 'h-10 w-10 rounded-lg',
        'icon-sm': 'h-8 w-8 rounded-lg',
        'icon-lg': 'h-12 w-12 rounded-xl',
      },
      rounded: {
        none: 'rounded-none',
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        '2xl': 'rounded-2xl',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      rounded: 'lg',
    },
  }
)

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariantsCVA> {
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, loading, leftIcon, rightIcon, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariantsCVA({ variant, size, rounded, className }))}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </button>
    )
  }
)
Button.displayName = 'Button'

// ================================================================================
// PREMIUM CARD COMPONENT
// ================================================================================
const cardVariantsCVA = cva(
  'bg-white dark:bg-neutral-900 rounded-xl overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border border-neutral-200 dark:border-neutral-800',
        glass: 'bg-white/80 dark:bg-neutral-900/80 backdrop-blur-xl border border-white/20 dark:border-neutral-700/30',
        elevated: 'shadow-hard border-0',
        outline: 'border-2 border-neutral-200 dark:border-neutral-700',
        gradient: 'bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 border-0',
      },
      hover: {
        none: '',
        lift: 'transition-all duration-200 hover:-translate-y-1 hover:shadow-medium',
        scale: 'transition-transform duration-200 hover:scale-[1.02]',
        glow: 'transition-all duration-200 hover:shadow-glow-primary',
      },
    },
    defaultVariants: {
      variant: 'default',
      hover: 'none',
    },
  }
)

interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariantsCVA> {}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, hover, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(cardVariantsCVA({ variant, hover, className }))}
        {...props}
      >
        {children}
      </div>
    )
  }
)
Card.displayName = 'Card'

// ================================================================================
// PREMIUM AVATAR COMPONENT
// ================================================================================
interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string
  alt?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  verified?: boolean
  status?: 'online' | 'offline' | 'busy' | 'away'
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, src, alt, size = 'md', verified, status, ...props }, ref) => {
    const sizeClasses = {
      xs: 'w-6 h-6 text-xs',
      sm: 'w-8 h-8 text-sm',
      md: 'w-10 h-10 text-base',
      lg: 'w-12 h-12 text-lg',
      xl: 'w-16 h-16 text-xl',
    }

    const statusColors = {
      online: 'bg-green-500',
      offline: 'bg-neutral-400',
      busy: 'bg-red-500',
      away: 'bg-yellow-500',
    }

    return (
      <div ref={ref} className={cn('relative inline-flex', className)} {...props}>
        <div
          className={cn(
            'relative rounded-full overflow-hidden bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center',
            sizeClasses[size]
          )}
        >
          {src ? (
            <img src={src} alt={alt || 'Avatar'} className="w-full h-full object-cover" />
          ) : (
            <span className="font-medium text-neutral-600 dark:text-neutral-300">
              {alt?.charAt(0).toUpperCase() || 'U'}
            </span>
          )}
        </div>
        {verified && (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-neutral-900">
            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        {status && (
          <div
            className={cn(
              'absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white dark:ring-neutral-900',
              statusColors[status]
            )}
          />
        )}
      </div>
    )
  }
)
Avatar.displayName = 'Avatar'

// ================================================================================
// SKELETON LOADER COMPONENT
// ================================================================================
interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'circular' | 'rectangular'
  width?: number | string
  height?: number | string
  animation?: 'pulse' | 'wave' | 'none'
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className,
  variant = 'rectangular',
  width,
  height,
  animation = 'pulse',
  ...props
}) => {
  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'animate-shimmer',
    none: '',
  }

  const variantClasses = {
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  }

  return (
    <div
      className={cn(
        'bg-neutral-200 dark:bg-neutral-700',
        variantClasses[variant],
        animationClasses[animation],
        className
      )}
      style={{ width, height }}
      {...props}
    />
  )
}

// ================================================================================
// PREMIUM BADGE COMPONENT
// ================================================================================
const badgeVariantsCVA = cva(
  'inline-flex items-center justify-center font-medium transition-colors',
  {
    variants: {
      variant: {
        default: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300',
        primary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
        secondary: 'bg-secondary-100 dark:bg-secondary-900/30 text-secondary-700 dark:text-secondary-300',
        accent: 'bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300',
        success: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
        warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
        danger: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
        info: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
        outline: 'border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300',
      },
      size: {
        sm: 'text-xs px-2 py-0.5 rounded',
        md: 'text-xs px-2.5 py-1 rounded-md',
        lg: 'text-sm px-3 py-1.5 rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
)

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariantsCVA> {}

export const Badge: React.FC<BadgeProps> = ({ className, variant, size, children, ...props }) => {
  return (
    <span className={cn(badgeVariantsCVA({ variant, size, className }))} {...props}>
      {children}
    </span>
  )
}

// ================================================================================
// PROGRESS BAR COMPONENT
// ================================================================================
interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number
  max?: number
  color?: 'primary' | 'secondary' | 'accent' | 'success' | 'warning' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
  animated?: boolean
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  className,
  value,
  max = 100,
  color = 'primary',
  size = 'md',
  showLabel = false,
  animated = false,
  ...props
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100))

  const colorClasses = {
    primary: 'bg-primary-500',
    secondary: 'bg-secondary-500',
    accent: 'bg-accent-500',
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
    danger: 'bg-red-500',
  }

  const sizeClasses = {
    sm: 'h-1.5',
    md: 'h-2.5',
    lg: 'h-4',
  }

  return (
    <div className={cn('w-full', className)} {...props}>
      <div className={cn('w-full bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden', sizeClasses[size])}>
        <motion.div
          className={cn('rounded-full', colorClasses[color], animated && 'animate-pulse')}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </div>
      {showLabel && (
        <div className="flex justify-between mt-1 text-xs text-muted-foreground">
          <span>{value}</span>
          <span>{max}</span>
        </div>
      )}
    </div>
  )
}

// ================================================================================
// TOGGLE/SWITCH COMPONENT
// ================================================================================
interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  description?: string
}

export const Switch: React.FC<SwitchProps> = ({ className, label, description, checked, onChange, ...props }) => {
  return (
    <label className={cn('flex items-center cursor-pointer', className)}>
      <div className="relative">
        <input type="checkbox" className="sr-only peer" checked={checked} onChange={onChange} {...props} />
        <div className="w-11 h-6 bg-neutral-200 dark:bg-neutral-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500" />
      </div>
      {(label || description) && (
        <div className="ml-3">
          {label && <span className="text-sm font-medium text-foreground">{label}</span>}
          {description && <span className="text-sm text-muted-foreground">{description}</span>}
        </div>
      )}
    </label>
  )
}

// ================================================================================
// EMPTY STATE COMPONENT
// ================================================================================
interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode
  title: string
  description?: string
  action?: React.ReactNode
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  className,
  icon,
  title,
  description,
  action,
  ...props
}) => {
  return (
    <div className={cn('flex flex-col items-center justify-center py-12 px-4 text-center', className)} {...props}>
      {icon && <div className="text-6xl mb-4">{icon}</div>}
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      {description && <p className="text-muted-foreground mb-6 max-w-md">{description}</p>}
      {action}
    </div>
  )
}

// ================================================================================
// TOOLTIP COMPONENT
// ================================================================================
interface TooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
}

export const Tooltip: React.FC<TooltipProps> = ({ content, children, position = 'top' }) => {
  const [show, setShow] = useState(false)

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  }

  return (
    <div className="relative inline-block" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={cn(
              'absolute z-50 px-3 py-1.5 text-sm bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 rounded-lg whitespace-nowrap',
              positionClasses[position]
            )}
          >
            {content}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ================================================================================
// TOAST/NOTIFICATION COMPONENT
// ================================================================================
interface ToastProps {
  message: string
  type?: 'success' | 'error' | 'warning' | 'info'
  onClose?: () => void
}

export const Toast: React.FC<ToastProps> = ({ message, type = 'info', onClose }) => {
  const typeClasses = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500',
  }

  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ',
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className={cn('fixed bottom-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg text-white shadow-lg', typeClasses[type])}
    >
      <span className="text-lg">{icons[type]}</span>
      <span>{message}</span>
      {onClose && (
        <button onClick={onClose} className="ml-2 hover:opacity-70">
          ✕
        </button>
      )}
    </motion.div>
  )
}

// ================================================================================
// DIVIDER COMPONENT
// ================================================================================
interface DividerProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: 'horizontal' | 'vertical'
  label?: string
}

export const Divider: React.FC<DividerProps> = ({ className, orientation = 'horizontal', label, ...props }) => {
  if (orientation === 'vertical') {
    return <div className={cn('w-px bg-neutral-200 dark:bg-neutral-700', className)} {...props} />
  }

  if (label) {
    return (
      <div className={cn('flex items-center gap-4', className)} {...props}>
        <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
      </div>
    )
  }

  return <div className={cn('h-px w-full bg-neutral-200 dark:bg-neutral-700', className)} {...props} />
}

// ================================================================================
// STAT CARD COMPONENT
// ================================================================================
interface StatCardProps {
  icon: React.ReactNode
  label: string
  value: string | number
  change?: number
  changeLabel?: string
  className?: string
}

export const StatCard: React.FC<StatCardProps> = ({ icon, label, value, change, changeLabel, className }) => {
  return (
    <Card className={cn('p-6', className)}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
          {change !== undefined && (
            <p className={cn('text-sm mt-2', change >= 0 ? 'text-green-500' : 'text-red-500')}>
              {change >= 0 ? '↑' : '↓'} {Math.abs(change)}% {changeLabel || 'from last month'}
            </p>
          )}
        </div>
        <div className="p-3 rounded-xl bg-primary-100 dark:bg-primary-900/30">
          {icon}
        </div>
      </div>
    </Card>
  )
}

export default {
  Button,
  Card,
  Avatar,
  Skeleton,
  Badge,
  ProgressBar,
  Switch,
  EmptyState,
  Tooltip,
  Toast,
  Divider,
  StatCard,
  formatCount,
  formatTimeAgo,
}